import {
  Bar,
  BarChart,
  CartesianGrid,
  LabelList,
  XAxis,
  YAxis,
} from "recharts";
import { useGroupClusters } from "src/lib/hooks/use-group-clusters";
import { useGroupSchools } from "src/lib/hooks/use-group-schools";
import { useGetClusters } from "src/lib/queries/use-get-clusters";
import { useGetCustomersData } from "src/lib/queries/use-get-customers-data";
import {
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
} from "src/components/shadcn/chart";
import { format } from "date-fns";
import { id } from "date-fns/locale";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "src/components/shadcn/tabs";
import { Badge } from "src/components/shadcn/badge";
import { formatNumber } from "src/components/numberingformat";

const chartConfig = {
  pagi: {
    label: "pagi",
    color: "var(--primary)",
  },
  siang: {
    label: "siang",
    color: "var(--secondary)",
  },
};

const getDeliveries = (customersData, item, type) => {
  const defaultDelivery = { morning: 0, afternoon: 0 };
  if (!customersData || !item) return defaultDelivery;

  const ids = type === "school" ? item.ids : item.kinds.map((kind) => kind.id);

  return customersData.reduce((acc, data) => {
    ids.forEach((id) => {
      const clusterTimeOfDelivery = data[`delivery_${id}`];
      const deliveryQty = data[`qty_${id}`];

      if (clusterTimeOfDelivery === 1) {
        acc.morning += deliveryQty;
      } else if (clusterTimeOfDelivery === 2) {
        acc.afternoon += deliveryQty;
      }
    });
    return acc;
  }, defaultDelivery);
};

export const RencanaProduksiChart = () => {
  return (
    <div className="flex flex-col gap-4 p-8 w-full">
      <div className="flex flex-col gap-2 items-center">
        <h2 className=" text-xl sm:text-2xl font-medium text-balance text-center">
          Rencana Produksi Hari Ini
        </h2>
        <p>Tanggal {format(new Date(), "PPP", { locale: id })}</p>
      </div>
      <Tabs className="w-full" defaultValue="school">
        <TabsList className="w-full">
          <TabsTrigger value="school">Sekolah</TabsTrigger>
          <TabsTrigger value="cluster">Cluster</TabsTrigger>
        </TabsList>
        {["cluster", "school"].map((type) => (
          <TabsContent key={type} value={type}>
            <Chart type={type} />
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
};

const Chart = ({ type }) => {
  const { data: clusters } = useGetClusters();

  const groupedClusters = useGroupClusters(clusters);
  const groupedSchools = useGroupSchools(clusters);

  const { data: customersData } = useGetCustomersData();

  const isSchool = type === "school";
  const tableData = isSchool ? groupedSchools : groupedClusters;

  const chartData = tableData.map((item) => {
    const deliveries = getDeliveries(customersData, item, type);
    return {
      name: isSchool ? item.name : item.label,
      pagi: deliveries.morning,
      siang: deliveries.afternoon,
      total: deliveries.morning + deliveries.afternoon,
    };
  });

  const totalDeliveries = chartData.reduce(
    (acc, data) => {
      acc.morning += data.pagi;
      acc.afternoon += data.siang;
      return acc;
    },
    {
      morning: 0,
      afternoon: 0,
    },
  );

  return (
    <div className="flex flex-col gap-2 relative group">
      <div className="absolute right-4 top-4 flex flex-col items-center gap-2 justify-center text-xs">
        <Badge className="flex items-center gap-2 w-full justify-between backdrop-blur-sm group-hover:opacity-0 transition-all ease-out z-10">
          <p className="font-medium">Pagi</p>
          <p>{formatNumber(totalDeliveries.morning)} pax</p>
        </Badge>
        <Badge
          variant="secondary"
          className="flex items-center gap-2 w-full justify-between backdrop-blur-sm group-hover:opacity-0 transition-all ease-out z-10"
        >
          <p className="font-medium">Siang</p>
          <p>{formatNumber(totalDeliveries.afternoon)} pax</p>
        </Badge>
      </div>
      <ChartContainer config={chartConfig} className="h-96 w-full">
        <BarChart accessibilityLayer data={chartData}>
          <CartesianGrid vertical={false} />
          <XAxis
            dataKey="name"
            tickLine={false}
            tickMargin={10}
            axisLine={false}
            tickFormatter={(value) => value.split("/")[0]}
          />
          <YAxis
            axisLine={false}
            tickLine={false}
            label={{ value: "Jumlah Pax", angle: -90, position: "insideLeft" }}
            domain={[0, (dataMax) => Math.ceil((dataMax * 1.4) / 100) * 100]}
            hide
          />
          <ChartTooltip content={<ChartTooltipContent />} />
          <ChartLegend content={<ChartLegendContent />} />
          <Bar
            dataKey="pagi"
            fill="var(--color-pagi)"
            radius={4}
            stackId="delivery"
          />
          <Bar
            dataKey="siang"
            fill="var(--color-siang)"
            radius={4}
            stackId="delivery"
          >
            <LabelList
              dataKey="total"
              position="top"
              formatter={formatNumber}
            />
          </Bar>
        </BarChart>
      </ChartContainer>
    </div>
  );
};
